import { CellClassParams, CellStyle, ColDef } from "ag-grid-community";
import { AgGridReact, type AgGridReactProps } from "ag-grid-react";
import { forwardRef, useMemo, useRef } from "react";

import { getCellRendererComponentFromMetadata } from "../utils/cell-renderer-registry";
import { getQuickFilterFormatter } from "../utils/grid-helpers";

export interface AgGridContainerProps extends AgGridReactProps {
  gridTheme?: string;
  height?: string;
  isSideBarEnabled?: boolean;
  /** Allow horizontal overflow (for grids with many/wide columns) */
  allowHorizontalOverflow?: boolean;
  /** When this key changes, refresh the locked column definitions without remounting the grid. */
  columnDefsVersionKey?: string;
}

export const AgGridContainer = forwardRef<AgGridReact, AgGridContainerProps>(
  (
    {
      gridTheme = "ag-theme-balham",
      height = "100%",
      isSideBarEnabled = false,
      allowHorizontalOverflow = false,
      columnDefsVersionKey,
      ...props
    },
    ref,
  ) => {
    const containerRef = useRef<HTMLDivElement>(null);

    // Process column definitions to apply cell renderers and formatters
    const processedColumns = useMemo(() => {
      const columns = props.columnDefs || [];
      return columns.map((col: ColDef) => {
        let nextCol = col;
        // Apply renderer mapping without clobbering column-defined filter/filterParams
        if (nextCol.cellRenderer && typeof nextCol.cellRenderer === "string") {
          const rendererDef = getCellRendererComponentFromMetadata(nextCol.cellRenderer, nextCol);
          const mergedFilter = nextCol.filter ?? rendererDef.filter;
          const mergedFilterParams = (() => {
            const a = rendererDef.filterParams as Record<string, unknown> | undefined;
            const b = nextCol.filterParams as Record<string, unknown> | undefined;
            if (!a && !b) {
              return undefined;
            }
            if (!a) {
              return b;
            }
            if (!b) {
              return a;
            }
            return { ...a, ...b };
          })();
          nextCol = {
            ...nextCol,
            ...rendererDef,
            cellStyle: rendererDef.cellStyle || nextCol.cellStyle,
            filter: mergedFilter,
            filterParams: mergedFilterParams,
          } as ColDef;
        }
        return nextCol;
      });
    }, [props.columnDefs]);

    // Apply quick filter formatting to processed columns (memoized to preserve column state on re-renders)
    const columns = useMemo(() => getQuickFilterFormatter(processedColumns), [processedColumns]);

    // Lock initial columnDefs after first compute so subsequent re-renders don't reset user column state
    const initialColumnsRef = useRef<ColDef[] | null>(null);
    const lockedVersionRef = useRef<string | undefined>(columnDefsVersionKey);
    if (lockedVersionRef.current !== columnDefsVersionKey) {
      if (columns?.length) {
        initialColumnsRef.current = columns;
      }
      lockedVersionRef.current = columnDefsVersionKey;
    }
    if (!initialColumnsRef.current && columns && columns.length) {
      initialColumnsRef.current = columns;
    }
    const lockedColumnDefs = initialColumnsRef.current || columns;

    const defaultColDefintion = useMemo(() => {
      return {
        editable: false,
        resizable: true,
        filter: true,
        sortable: true,
        minWidth: 200,
        flex: 1,
        cellStyle: (_params: CellClassParams): CellStyle => ({
          display: "flex",
          alignItems: "center",
        }),
        filterParams: {
          buttons: ["reset", "apply"],
          closeOnApply: true,
          newRowsAction: "keep",
        },
        sortNullsFirst: false,
        ...props.defaultColDef,
      };
    }, [props.defaultColDef]);

    const sideBar = {
      toolPanels: [
        {
          id: "filters",
          labelDefault: "Filters",
          labelKey: "filters",
          iconKey: "filter",
          toolPanel: "agFiltersToolPanel",
          toolPanelParams: {
            suppressColumnSelectAll: true,
            suppressFilterSearch: false,
          },
        },
        {
          id: "columns",
          labelDefault: "Columns",
          labelKey: "columns",
          iconKey: "columns",
          toolPanel: "agColumnsToolPanel",
          toolPanelParams: {
            suppressRowGroups: true,
            suppressValues: true,
            suppressPivots: true,
            suppressPivotMode: true,
            suppressSideButtons: false,
            suppressColumnSelectAll: false,
            suppressColumnExpandAll: false,
          },
        },
      ],
    };
    return (
      <div
        ref={containerRef}
        className={gridTheme}
        style={{
          width: "100%",
          display: "flex",
          height: height,
          flexDirection: "column",
          border: "1px solid #e0e0e0",
          borderRadius: "4px",
          // Use 'auto' overflow when horizontal scrolling is needed (e.g., Assistant grids with many columns)
          // Otherwise use 'hidden' to prevent unexpected scroll behavior
          overflow: allowHorizontalOverflow ? "auto" : "hidden",
        }}
      >
        <AgGridReact
          ref={ref}
          {...props}
          columnDefs={lockedColumnDefs}
          defaultColDef={defaultColDefintion}
          domLayout="normal"
          sideBar={isSideBarEnabled ? sideBar : false}
          suppressDragLeaveHidesColumns={true}
        />
      </div>
    );
  },
);

AgGridContainer.displayName = "AgGridContainer";
